import { useState, useEffect } from "react";
import axios from "axios";
import {
  Store,
  Link as LinkIcon,
  AlertCircle,
  CheckCircle2,
  Settings,
  Key,
  Globe,
  RefreshCw,
  Power,
  Clock,
  TrendingUp,
  Package,
  Percent,
  Loader2,
  Save
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import API from "@/utils/api";

const INTEGRATIONS = [
  {
    id: "just-eat",
    name: "Just Eat",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Just_Eat_2020_logo.svg/1200px-Just_Eat_2020_logo.svg.png",
    color: "from-orange-500 to-orange-600",
    status: "pending",
    description: "Recibe pedidos directamente desde Just Eat",
    features: ["Sincronización de menú", "Pedidos en tiempo real", "Actualización de estado"]
  },
  {
    id: "uber-eats",
    name: "Uber Eats",
    logo: "https://d3i4yxtzktqr9n.cloudfront.net/web-eats-v2/ee037afa799a53c9.svg",
    color: "from-green-500 to-green-600",
    status: "pending",
    description: "Gestiona pedidos de Uber Eats desde un solo lugar",
    features: ["Menú automático", "Tracking de repartidores", "Estadísticas detalladas"]
  },
  {
    id: "glovo",
    name: "Glovo",
    logo: "https://glovoapp.com/images/glovo-logo.svg",
    color: "from-yellow-500 to-yellow-600",
    status: "pending",
    description: "Integra Glovo con tu sistema de pedidos",
    features: ["Multi-tienda", "Menú Pro", "Promociones sincronizadas"]
  }
];

export default function AdminIntegrations() {
  const [integrations, setIntegrations] = useState(INTEGRATIONS);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [autoSync, setAutoSync] = useState(true);

  const [deliveryConfig, setDeliveryConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/delivery/config`, { withCredentials: true });
        setDeliveryConfig(response.data.local);
      } catch (error) {
        console.error("Error fetching delivery config:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleConnect = (integrationId) => {
    toast.info("Funcionalidad en desarrollo", {
      description: "Las integraciones con plataformas de terceros estarán disponibles próximamente cuando nos proporcionen las APIs."
    });
  };

  const handleDisconnect = (integrationId) => {
    setIntegrations(integrations.map(int =>
      int.id === integrationId ? { ...int, status: "pending" } : int
    ));
    toast.success("Integración desconectada");
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      await axios.patch(`${API}/admin/delivery/config`, deliveryConfig, { withCredentials: true });
      toast.success("Configuración de comisiones guardada");
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "connected":
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Conectado</Badge>;
      case "error":
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
      case "pending":
        return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="w-3 h-3 mr-1" />Pendiente API</Badge>;
      default:
        return <Badge variant="secondary">Desconectado</Badge>;
    }
  };

  return (
    <AdminLayout title="Integraciones">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Integraciones</h1>
          <p className="text-gray-400">
            Conecta con plataformas de delivery para recibir pedidos en un solo lugar
          </p>
        </div>

        {/* Alert Banner */}
        <Card className="border-yellow-500/20 bg-yellow-500/10">
          <CardContent className="flex items-start gap-3 pt-6">
            <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-500 mb-1">APIs en Proceso</h3>
              <p className="text-sm text-gray-300">
                Las integraciones con Just Eat, Uber Eats y Glovo estarán disponibles una vez que
                las plataformas nos proporcionen acceso a sus APIs. Mientras tanto, puedes preparar
                tu configuración.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Integrations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {integrations.map((integration) => (
            <Card
              key={integration.id}
              className="border-gray-800 hover:border-gray-700 transition-all cursor-pointer"
              onClick={() => setSelectedIntegration(integration)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${integration.color} flex items-center justify-center`}>
                    <Store className="w-8 h-8 text-white" />
                  </div>
                  {getStatusBadge(integration.status)}
                </div>
                <CardTitle className="text-white mt-4">{integration.name}</CardTitle>
                <CardDescription>{integration.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {integration.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      {feature}
                    </div>
                  ))}
                </div>
                <Button
                  variant={integration.status === "connected" ? "outline" : "default"}
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnect(integration.id);
                  }}
                >
                  {integration.status === "connected" ? (
                    <>
                      <Power className="w-4 h-4 mr-2" />
                      Desconectar
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Conectar
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Configuration Panel */}
        <Card className="border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Settings className="w-5 h-5" />
              Configuración de Integraciones
            </CardTitle>
            <CardDescription>
              Prepara las credenciales para cuando las APIs estén disponibles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-gray-900">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="commissions">Comisiones</TabsTrigger>
                <TabsTrigger value="api">API Keys</TabsTrigger>
                <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
              </TabsList>

              <TabsContent value="commissions" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg flex gap-3 mb-4">
                    <Percent className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-300">
                      Configura las comisiones que te cobran las plataformas externas.
                      Estos valores se usarán para calcular el <strong>importe neto</strong> en tus reportes de caja.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Glovo (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Ej: 30"
                        value={deliveryConfig?.glovo_commission ? (deliveryConfig.glovo_commission * 100).toFixed(2) : ""}
                        onChange={(e) => setDeliveryConfig({ ...deliveryConfig, glovo_commission: parseFloat(e.target.value) / 100 })}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Uber Eats (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Ej: 30"
                        value={deliveryConfig?.ubereats_commission ? (deliveryConfig.ubereats_commission * 100).toFixed(2) : ""}
                        onChange={(e) => setDeliveryConfig({ ...deliveryConfig, ubereats_commission: parseFloat(e.target.value) / 100 })}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Just Eat (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Ej: 30"
                        value={deliveryConfig?.justeat_commission ? (deliveryConfig.justeat_commission * 100).toFixed(2) : ""}
                        onChange={(e) => setDeliveryConfig({ ...deliveryConfig, justeat_commission: parseFloat(e.target.value) / 100 })}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                  </div>

                  <Button onClick={handleSaveConfig} className="w-full bg-blue-600 hover:bg-blue-500" disabled={isSaving}>
                    {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar Comisiones
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-white">Sincronización Automática</Label>
                      <p className="text-sm text-gray-400">
                        Sincronizar menú y precios automáticamente
                      </p>
                    </div>
                    <Switch checked={autoSync} onCheckedChange={setAutoSync} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-white">Actualización de Disponibilidad</Label>
                      <p className="text-sm text-gray-400">
                        Actualizar disponibilidad de platos en tiempo real
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-white">Notificaciones de Pedidos</Label>
                      <p className="text-sm text-gray-400">
                        Recibir notificaciones push de nuevos pedidos
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="api" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">Just Eat API Key</Label>
                    <Input
                      placeholder="Ingresa tu API key de Just Eat"
                      className="mt-2"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Uber Eats Client ID</Label>
                    <Input
                      placeholder="Ingresa tu Client ID de Uber Eats"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Glovo API Token</Label>
                    <Input
                      placeholder="Ingresa tu API token de Glovo"
                      className="mt-2"
                    />
                  </div>
                  <Button onClick={handleSaveConfig} className="w-full">
                    <Key className="w-4 h-4 mr-2" />
                    Guardar Credenciales
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="webhooks" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">Webhook URL</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="https://api.tu-dominio.com/webhooks/orders"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        className="flex-1"
                      />
                      <Button variant="outline" size="icon">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">
                      Esta URL recibirá notificaciones de nuevos pedidos
                    </p>
                  </div>
                  <div className="space-y-2 p-4 bg-gray-900 rounded-lg">
                    <h4 className="font-semibold text-white text-sm">Eventos Disponibles</h4>
                    <div className="space-y-1 text-sm text-gray-400">
                      <div>• order.created - Nuevo pedido recibido</div>
                      <div>• order.accepted - Pedido aceptado</div>
                      <div>• order.ready - Pedido listo para entrega</div>
                      <div>• order.delivered - Pedido entregado</div>
                      <div>• order.cancelled - Pedido cancelado</div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Statistics Preview */}
        <Card className="border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <TrendingUp className="w-5 h-5" />
              Vista Previa de Estadísticas
            </CardTitle>
            <CardDescription>
              Cuando las integraciones estén activas, verás estas métricas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Pedidos Just Eat</p>
                    <p className="text-2xl font-bold text-white">---</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Pedidos Uber Eats</p>
                    <p className="text-2xl font-bold text-white">---</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Pedidos Glovo</p>
                    <p className="text-2xl font-bold text-white">---</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
